// One record, opened from any view: every field, approvals, comments with @mentions, and its history with undo.
import { useEffect, useMemo, useRef, useState } from "react";
import type { Approval, Field, GridEvent, PersonRow, Row, RowsResponse, TableData, Value } from "./pApi";
import { pApi } from "./pApi";
import { ApprovalBadge, Avatar, CellValue } from "./Cell";
import { FieldInput } from "./Editors";
import { EDITABLE, TYPE_INFO, fmtDateTime, text } from "./format";
import { ago } from "../ui";

type Props = {
  rowId: string; data: TableData; me: string | null; people: PersonRow[];
  onEdit: (rowId: string, values: Record<string, unknown>) => Promise<void> | void;
  onApply: (r: RowsResponse) => void; onDelete: (id: string) => void; onClose: () => void;
  fieldsOf: (tableId: string) => Field[] | undefined;
  readOnlyKeys?: string[]; nav?: { prev?: () => void; next?: () => void };
};

export function RecordPanel(p: Props) {
  const row = p.data.rows.find((r) => r.id === p.rowId);
  const [events, setEvents] = useState<GridEvent[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [mention, setMention] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const can = p.data.table.canEdit;
  const load = () => pApi.row(p.rowId).then((d) => setEvents(d.events)).catch((e) => setErr(e.message));
  useEffect(() => { setEvents(null); load(); ref.current?.focus(); }, [p.rowId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Refresh the history a moment after the record changes.
  const stamp = row?.updatedAt;
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, [stamp]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) p.onClose();
      if ((e.altKey) && e.key === "ArrowDown") p.nav?.next?.();
      if ((e.altKey) && e.key === "ArrowUp") p.nav?.prev?.();
    };
    document.addEventListener("keydown", k); return () => document.removeEventListener("keydown", k);
  }, [p]);

  const fields = p.data.fields;
  const primary = fields.find((f) => f.primary) ?? fields[0];
  const byKey = useMemo(() => new Map(fields.map((f) => [f.key, f])), [fields]);
  if (!row) return (
    <div className="peek-wrap" onClick={p.onClose}><div className="peek" onClick={(e) => e.stopPropagation()}><div className="peek-h"><h2>That record is no longer here</h2><button className="btn" onClick={p.onClose}>Close</button></div></div></div>
  );
  const edit = async (f: Field, v: unknown) => { setErr(null); try { await p.onEdit(row.id, { [f.key]: v }); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } };
  const title = primary ? text(primary, row.values[primary.key], p.data.links) || `Record ${row.number}` : `Record ${row.number}`;

  const send = async () => {
    const b = comment.trim(); if (!b) return;
    setErr(null);
    try { const ev = await pApi.comment(row.id, b); setComment(""); setEvents((e) => [...(e ?? []), ev]); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };
  const undo = async (id: string) => { setErr(null); try { p.onApply(await pApi.undo(id)); load(); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } };
  const mentionList = mention !== null ? p.people.filter((x) => x.name.toLowerCase().includes(mention.toLowerCase())).slice(0, 6) : [];

  return (
    <div className="peek-wrap" onMouseDown={(e) => { if (e.target === e.currentTarget) p.onClose(); }}>
      <div className="peek pv-rec" ref={ref} tabIndex={-1} role="dialog" aria-label={title}>
        <div className="peek-h">
          <div style={{ minWidth: 0 }}>
            <div className="peek-sub">{p.data.table.name} · record {row.number}</div>
            <h2 style={{ overflowWrap: "anywhere" }}>{title}</h2>
            <div className="peek-sub">Added {fmtDateTime(row.createdAt)}{row.createdByName ? ` by ${row.createdByName}` : ""} · changed {ago(row.updatedAt)}{row.updatedByName ? ` by ${row.updatedByName}` : ""}</div>
          </div>
          <div className="btn-row" style={{ flexWrap: "nowrap" }}>
            {p.nav?.prev && <button className="btn" aria-label="Previous record (Alt+Up)" title="Previous record (Alt+Up)" onClick={p.nav.prev}>↑</button>}
            {p.nav?.next && <button className="btn" aria-label="Next record (Alt+Down)" title="Next record (Alt+Down)" onClick={p.nav.next}>↓</button>}
            {can && <button className="btn" onClick={() => { if (confirm(`Delete “${title}”? You can restore it from its history.`)) p.onDelete(row.id); }}>Delete</button>}
            <button className="btn" onClick={p.onClose}>Close</button>
          </div>
        </div>
        <div className="pv-rec-body">
          {err && <div className="msg error" role="alert">{err}</div>}
          <div className="pv-rec-fields">
            {fields.map((f) => {
              const ro = !can || !EDITABLE(f) || (p.readOnlyKeys ?? []).includes(f.key);
              return (
                <div key={f.key} className="field pv-rec-f">
                  <label htmlFor={`rf-${f.key}`}><span className="pg-ti" aria-hidden="true">{TYPE_INFO[f.type].icon}</span> {f.name}{f.type !== "approval" && !EDITABLE(f) && <span className="muted" style={{ fontWeight: 400 }}> · calculated</span>}{(p.readOnlyKeys ?? []).includes(f.key) && <span className="muted" style={{ fontWeight: 400 }}> · from its subtasks</span>}</label>
                  {f.type === "approval" ? <ApprovalBox f={f} row={row} me={p.me} people={p.people} canEdit={can} onApply={(r) => { p.onApply(r); load(); }} onError={setErr} />
                    : ro ? <div className="pv-rec-ro">{(row.values[f.key] ?? null) === null ? <span className="muted">—</span> : <CellValue f={f} v={row.values[f.key] as Value} links={p.data.links} allFields={fields} fieldsOf={p.fieldsOf} />}{f.issue && <div className="hint" style={{ color: "var(--danger-fg)" }}>{f.issue}</div>}</div>
                    : <FieldInput id={`rf-${f.key}`} f={f} value={row.values[f.key] as Value} links={p.data.links} people={p.people} selfId={row.id} onChange={(v) => edit(f, v)} />}
                  {f.description && <span className="hint">{f.description}</span>}
                </div>
              );
            })}
          </div>

          <h3 className="pv-rec-h">Comments and history</h3>
          <div className="pv-comment">
            <textarea className="textarea" rows={2} placeholder="Add a comment. Type @ to mention someone." value={comment}
              onChange={(e) => { setComment(e.target.value); const m = /@([\w .'-]{0,30})$/.exec(e.target.value.slice(0, e.target.selectionStart)); setMention(m ? m[1] : null); }}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }} />
            {mentionList.length > 0 && (
              <ul className="pv-mention">
                {mentionList.map((x) => <li key={x.id}><button onMouseDown={(e) => { e.preventDefault(); setComment((c) => c.replace(/@([\w .'-]{0,30})$/, `@${x.name} `)); setMention(null); }}><Avatar name={x.name} />{x.name}</button></li>)}
              </ul>
            )}
            <div className="btn-row" style={{ justifyContent: "flex-end", marginTop: 6 }}><button className="btn primary" disabled={!comment.trim()} onClick={send}>Comment</button></div>
          </div>
          {!events ? <div className="empty">Loading…</div> : (
            <ol className="pv-hist">
              {[...events].reverse().map((e) => <HistoryItem key={e.id} e={e} byKey={byKey} data={p.data} canUndo={can} onUndo={() => undo(e.id)} />)}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function HistoryItem({ e, byKey, data, canUndo, onUndo }: { e: GridEvent; byKey: Map<string, Field>; data: TableData; canUndo: boolean; onUndo: () => void }) {
  const who = e.actorName ?? "Someone";
  let body: React.ReactNode = null;
  if (e.kind === "comment") body = <div className="pv-hist-c">{e.body}</div>;
  else if (e.kind === "created") body = <span>added this record</span>;
  else if (e.kind === "deleted") body = <span>deleted this record</span>;
  else if (e.kind === "restored") body = <span>restored this record</span>;
  else if (e.kind === "notify") body = <span>notified {e.detail?.to?.name ?? "someone"}: {e.body}</span>;
  else if (e.kind === "automation") body = <span className={e.detail?.failed ? "pv-bad" : undefined}>{e.body}</span>;
  else if (e.kind === "approval") body = <span>{e.detail?.to === "pending" ? <>asked {e.detail?.approver?.name ?? "someone"} to approve</> : e.detail?.to === "approved" ? "approved it" : "rejected it"}{e.body ? <>: “{e.body}”</> : null}{e.detail?.reason ? <span className="muted"> · {e.detail.reason}</span> : null}</span>;
  else if (e.kind === "edit") {
    if (e.detail?.parent) body = <span>{e.detail.parent[1] ? "moved it under another task" : "moved it out to the top level"}</span>;
    else {
      const ch = Object.entries((e.detail?.changes ?? {}) as Record<string, [Value, Value]>);
      body = (
        <span>
          {e.detail?.reason ? <span className="muted">{e.detail.reason}: </span> : null}
          {ch.map(([k, [a, b]], i) => {
            const f = byKey.get(k);
            if (!f) return <span key={k}>{i ? "; " : ""}changed a field that's been deleted</span>;
            const from = text(f, a, data.links), to = text(f, b, data.links);
            return <span key={k}>{i ? "; " : ""}<strong>{f.name}</strong> {from ? <><s className="muted">{from}</s> → </> : "set to "}{to || <span className="muted">empty</span>}</span>;
          })}
          {canUndo && !e.detail?.undone && e.detail?.reason !== "Undo" && ch.length > 0 && <button className="linkish pv-undo" onClick={onUndo}>Undo</button>}
          {e.detail?.undone && <span className="muted"> · undone</span>}
        </span>
      );
    }
  }
  return (
    <li className={`pv-hist-i ${e.kind}`}>
      <Avatar name={who} size={22} />
      <div><div className="pv-hist-m"><strong>{who}</strong> <span className="muted" title={fmtDateTime(e.at)}>{ago(e.at)}</span></div>{body}</div>
    </li>
  );
}

function ApprovalBox({ f, row, me, people, canEdit, onApply, onError }: { f: Field; row: Row; me: string | null; people: PersonRow[]; canEdit: boolean; onApply: (r: RowsResponse) => void; onError: (s: string | null) => void }) {
  const a = row.values[f.key] as Approval | null | undefined;
  const [asking, setAsking] = useState(false);
  const [who, setWho] = useState("");
  const [note, setNote] = useState("");
  const run = async (fn: () => Promise<RowsResponse>) => { onError(null); try { onApply(await fn()); setAsking(false); setNote(""); } catch (e) { onError(e instanceof Error ? e.message : String(e)); } };
  const mine = a?.status === "pending" && a.approverId === me;
  return (
    <div className="pv-appr">
      {a ? <div className="btn-row"><ApprovalBadge a={a} />{a.note && <span className="muted">“{a.note}”</span>}<span className="muted" style={{ fontSize: 12 }}>asked by {a.requestedByName ?? "someone"} {ago(a.at)}</span></div> : <span className="muted">Not asked yet</span>}
      {mine && (
        <div className="pv-appr-act">
          <input className="input" placeholder="Note (needed to reject)" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="btn-row">
            <button className="btn primary" onClick={() => run(() => pApi.decide(row.id, { field: f.key, decision: "approved", note: note || undefined }))}>Approve</button>
            <button className="btn" onClick={() => run(() => pApi.decide(row.id, { field: f.key, decision: "rejected", note }))}>Reject</button>
          </div>
        </div>
      )}
      {canEdit && !mine && (asking ? (
        <div className="pv-appr-act">
          <select className="select" value={who} onChange={(e) => setWho(e.target.value)} aria-label="Who approves"><option value="">Who approves it?</option>{people.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>
          <input className="input" placeholder="Note for them (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="btn-row"><button className="btn primary" disabled={!who} onClick={() => run(() => pApi.requestApproval(row.id, { field: f.key, approver: who, note: note || undefined }))}>Ask for approval</button><button className="btn" onClick={() => setAsking(false)}>Cancel</button></div>
        </div>
      ) : <button className="btn" onClick={() => setAsking(true)}>{a ? "Ask again" : "Ask for approval"}</button>)}
    </div>
  );
}
